import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormGroup } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { cloneDeep, isEmpty } from 'lodash';
import { getSuppliers as getSuppliersAction } from '../../Dashboard/TodoList/CostingTsx/store/Costing.action';
import * as placeholderAction from '../../PlaceholderManagement/store/PlaceHolderManagement.action';
import { getCatBasedOnPT } from '../../SystemMasters/Store/SystemMasters.action';
import CommonMultiSelect from '../../../Components/Utility/FilterAction/CommonMultiSelect';

function UserLeftSection({ data, leftSectionData, submitFlag, setParentDetails, setShowErrorMsg, mainState }) {
  const { brandVal, catOneVal, catTwoVal, supplierVal, productVal } = mainState;
  const reducer = useSelector((reducerstate) => reducerstate);
  const str = data?.brand?.map((ele) => ele?._id);
  const { UserDetails } = reducer;
  const { userInfo } = UserDetails;
  const loggedInUserId = userInfo.userId;
  const dispatch = useDispatch();
  const [brandsList, setBrandsList] = useState([]);
  const [catOneList, setCatOneList] = useState([]);
  const [catTwoList, setCatTwoList] = useState([]);
  const [supplierList, setSuppliersList] = useState([]);
  const [productTypeList, setProductTypeList] = useState([]);
  const [selectedCatOne, setSelectedCataOne] = useState([]);
  const [selectedCatTwo, setSelectedCatTwo] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(str);
  const [selectedSupplier, setSelectedSupplier] = useState([]);
  const [selectedProductType, setSelectedProductType] = useState([]);
  const { t } = useTranslation('common');
  const [groupsDetails, setGroupsDetails] = useState({});
  const [editMode, setEditMode] = useState(false);
  const [catBasedOnPTData, setCatBasedOnPTData] = useState(false);

  useEffect(() => {
    const fetchCatBasedOnPTData = async () => {
      try {
        const res = await dispatch(getCatBasedOnPT());
        setCatBasedOnPTData(!!res);
      } catch (e) {
        setCatBasedOnPTData(false);
      }
    };
    fetchCatBasedOnPTData();
  }, [dispatch]);

  // Compute filtered Category 1 based on selected Product Type when catBasedOnPTData is enabled
  const filteredCatOneList = useMemo(() => {
    if (!catBasedOnPTData) return catOneList || [];
    const ptIds = (Array.isArray(selectedProductType) ? selectedProductType : []).map((v) => v?._id || v?.value || v).filter(Boolean);
    // Return empty array if no product type is selected (unless allProductTypes is checked)
    if (!ptIds.length && !groupsDetails?.allProductTypes) return [];
    // If allProductTypes is checked, return all categories
    if (groupsDetails?.allProductTypes) return catOneList || [];
    return (catOneList || []).filter((c1) => {
      const rel = c1?.productType || [];
      const arr = Array.isArray(rel) ? rel : [rel];
      return arr.some((id) => ptIds.includes(id?._id || id));
    });
  }, [catBasedOnPTData, catOneList, selectedProductType, groupsDetails?.allProductTypes]);

  // Compute filtered Category 2 based on selected Category 1 when catBasedOnPTData is enabled
  const filteredCatTwoList = useMemo(() => {
    if (!catBasedOnPTData) return catTwoList || [];

    // If allCategories is checked, return all category 2 options
    if (groupsDetails?.allCategories) return catTwoList || [];

    // Get selected category 1 IDs
    const c1Ids = (Array.isArray(selectedCatOne) ? selectedCatOne : []).map((v) => v?._id || v?.value || v).filter(Boolean);

    // Return empty array if no category 1 is selected
    if (!c1Ids.length) return [];

    // Filter category 2 based on selected category 1
    return (catTwoList || []).filter((c2) => {
      const c1Rel = c2?.categoryOneId || c2?.catOneIds || [];
      const a1 = Array.isArray(c1Rel) ? c1Rel : [c1Rel];
      return a1.some((id) => c1Ids.includes(id?._id || id));
    });
  }, [catBasedOnPTData, catTwoList, selectedCatOne, groupsDetails?.allCategories]);

  useEffect(() => {
    setGroupsDetails(data);
    setEditMode(true);
  }, [data]);

  useEffect(() => {
    setSelectedBrand(data?.brand || []);
    setSelectedCataOne(data?.catOne || []);
    setSelectedCatTwo(data?.catTwo || []);
    setSelectedSupplier(data?.supplier || []);
    setSelectedProductType(data?.productType || []);
  }, [editMode, data]);

  // Clear categories when catBasedOnPTData is true and conditions are not met
  useEffect(() => {
    if (!catBasedOnPTData || !groupsDetails) return;

    // Clear Category 1 if no Product Type selected and allProductTypes is false
    if (!groupsDetails?.allProductTypes && (!selectedProductType || selectedProductType.length === 0) && selectedCatOne?.length > 0) {
      setSelectedCataOne([]);
      setParentDetails((oldData) => ({ ...oldData, catOne: [] }));
    }

    // Clear Category 2 if no Category 1 selected and allCategories is false
    if (!groupsDetails?.allCategories && (!selectedCatOne || selectedCatOne.length === 0) && selectedCatTwo?.length > 0) {
      setSelectedCatTwo([]);
      setParentDetails((oldData) => ({ ...oldData, catTwo: [] }));
    }
  }, [catBasedOnPTData, groupsDetails, selectedProductType, selectedCatOne, selectedCatTwo, setParentDetails]);

  const firstExuit = useCallback(async () => {
    const category1 = await getCategoryOne();
    const category2 = await getCategoryTwo();
    const brands = await getBrands();
    const productTypes = await getProductTypes();
    const suppliers = await getSuppliers();
    setBrandsList(brands);
    setCatOneList(category1);
    setCatTwoList(category2);
    setSuppliersList(suppliers);
    setProductTypeList(productTypes);
  }, []);
  useEffect(() => {
    firstExuit();
  }, [firstExuit]);

  const getBrands = async () => {
    let brandsList = [];
    brandsList = await dispatch(
      placeholderAction.fetchAllBrandAction({
        accessedFrom: 'masters',
        loggedInUserId,
      }),
    );
    brandsList = brandsList.map((e) => {
      e.label = e?.name;
      e.value = e?._id;
      return e;
    });
    return brandsList;
  };

  const getCategoryOne = async () => {
    let catOneList = [];
    catOneList = await dispatch(
      placeholderAction.fetchCategoriesAction({
        accessedFrom: 'masters',
        categoryType: 1,
        loggedInUserId,
      }),
    );

    catOneList = catOneList.map((e) => {
      e.label = e?.name;
      e.value = e?._id;
      return e;
    });
    return catOneList;
  };

  const getCategoryTwo = async () => {
    let catTwoList = [];
    catTwoList = await dispatch(
      placeholderAction.fetchCategoriesAction({
        accessedFrom: 'masters',
        categoryType: 2,
        loggedInUserId,
      }),
    );

    catTwoList = catTwoList?.map((e) => {
      e.label = e?.name;
      e.value = e?._id;
      return e;
    });
    return catTwoList;
  };

  const getSuppliers = async () => {
    let supplierList = await dispatch(
      getSuppliersAction({
        loggedInUserId,
      }),
    );

    supplierList = supplierList?.map((e) => {
      e.label = e?.name;
      e.value = e?._id;
      return e;
    });
    return supplierList;
  };

  const getProductTypes = async () => {
    let ptList = [];
    ptList = await dispatch(
      placeholderAction.fetchAllProductType({
        accessedFrom: 'masters',
        loggedInUserId,
        tier: 'Tier1',
      }),
    );

    ptList = ptList?.map((e) => {
      e.label = e?.name;
      e.value = e?._id;
      return e;
    });
    return ptList;
  };

  const SelectedBrand = async (selected) => {
    setParentDetails((oldData) => ({ ...oldData, brand: selected }));
  };
  const SelectedCataOne = async (selected) => {
    setParentDetails((oldData) => ({ ...oldData, catOne: selected }));
    if (catBasedOnPTData) {
      // Clear Category 2 when Category 1 changes under PT-based dependency
      setSelectedCatTwo([]);
      setParentDetails((oldData) => ({ ...oldData, catTwo: [] }));
      setGroupsDetails((prev) => ({ ...prev, catTwo: [] }));
    }
  };
  const SelectedCatTwo = async (selected) => {
    setParentDetails((oldData) => ({ ...oldData, catTwo: selected }));
  };
  const SelectedSupplier = async (selected) => {
    setParentDetails((oldData) => ({ ...oldData, supplier: selected }));
  };
  const SelectedProductType = async (selected) => {
    setParentDetails((oldData) => ({ ...oldData, productType: selected }));
    if (catBasedOnPTData) {
      // Clear Category 1 and 2 when Product Type changes under PT-based dependency
      setSelectedCataOne([]);
      setSelectedCatTwo([]);
      setParentDetails((oldData) => ({ ...oldData, catOne: [], catTwo: [] }));
      setGroupsDetails((prev) => ({ ...prev, catOne: [], catTwo: [] }));
    }
  };

  const setValidation = () => {
    if (
      submitFlag &&
      ((!groupsDetails?.allBrands && isEmpty(selectedBrand)) ||
        (!groupsDetails?.allCategories && (isEmpty(selectedCatTwo) || isEmpty(selectedCatOne))) ||
        (!groupsDetails?.allSuppliers && isEmpty(selectedSupplier)) ||
        (!groupsDetails?.allProductTypes && isEmpty(selectedProductType)))
    ) {
      setShowErrorMsg(true);
    }
  };

  const checkboxHandler = (e) => {
    setValidation();
    const name = e.target.id;
    const value = e.target.checked;
    switch (name) {
      case 'allBrands':
        if (value === true) {
          const check = { ...groupsDetails };
          check[name] = value;
          check.brand = [];
          setGroupsDetails(check);
          setSelectedBrand([]);
        } else {
          const check = { ...groupsDetails };
          check[name] = value;
          setGroupsDetails(check);
        }
        setParentDetails((previousData) => ({ ...previousData, brand: [], allBrands: value }));
        break;
      case 'allCategories':
        if (value === true) {
          const check = { ...groupsDetails };
          check[name] = value;
          check.catOne = [];
          check.catTwo = [];

          setGroupsDetails(check);
          setSelectedCataOne([]);
          setSelectedCatTwo([]);
        } else {
          const check = { ...groupsDetails };
          check[name] = value;
          setGroupsDetails(check);
        }
        setParentDetails((previousData) => ({ ...previousData, catOne: [], catTwo: [], allCategories: value }));

        break;
      case 'allSuppliers':
        if (value === true) {
          const check = { ...groupsDetails };
          check[name] = value;
          check.supplier = [];
          setGroupsDetails(check);
          setSelectedSupplier([]);
        } else {
          const check = { ...groupsDetails };
          check[name] = value;
          setGroupsDetails(check);
        }
        setParentDetails((previousData) => ({ ...previousData, supplier: [], allSuppliers: value }));

        break;
      case 'allProductTypes':
        if (value === true) {
          const check = { ...groupsDetails };
          check[name] = value;
          check.productType = [];
          setGroupsDetails(check);
          setSelectedProductType([]);
        } else {
          const check = { ...groupsDetails };
          check[name] = value;
          setGroupsDetails(check);
        }
        setParentDetails((previousData) => ({ ...previousData, productType: [], allProductTypes: value }));
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    const assignments = {
      allBrands: groupsDetails?.allBrands,
      allCategories: groupsDetails?.allCategories,
      allSuppliers: groupsDetails?.allSuppliers,
      allProductTypes: groupsDetails?.allProductTypes,
      brand: groupsDetails?.brand,
      catOne: groupsDetails?.catOne,
      catTwo: groupsDetails?.catTwo,
      supplier: groupsDetails?.supplier,
      productType: groupsDetails?.productType,
      catBasedOnPTData,
      archieveTechPack: groupsDetails?.archieveTechPack,
      deleteTechPack: groupsDetails?.deleteTechPack,
      downloadTechPack: groupsDetails?.downloadTechPack,
      openTechPack: groupsDetails?.openTechPack,
      submitTechPack: groupsDetails?.submitTechPack,
      trashTechPack: groupsDetails?.trashTechPack,
    };
    leftSectionData(assignments);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsDetails]);

  const switchTechPackHandler = async (value, name) => {
    const allPackState = cloneDeep(groupsDetails);
    allPackState[name] = value;
    setGroupsDetails(allPackState);
  };

  return (
    <div>
      <div className="mt-4">
        <h6>
          {t('USER_LEFT_SECTION.USER_GROUP')}: {groupsDetails?.name}
        </h6>
      </div>
      <div className="table-responsive table-max-h-60 pb-2 pb-4">
        <table className="table custom-table font-13">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontWeight: '500' }}>{t('USER_LEFT_SECTION.BRAND_AND_CATEGORY_ASSIGNMENT')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="checkbox-label margin-auto mx-4 my-2">
                  <input type="checkbox" onClick={(e) => checkboxHandler(e)} defaultChecked={groupsDetails?.allBrands} id="allBrands" />
                  <label htmlFor="allBrands" className="chklabel">
                    <span className="mx-2">{t('USER_LEFT_SECTION.FULL_ACCESS_BRANDS')}</span>
                  </label>
                </div>
                <div className="checkbox-label margin-auto mx-4 my-2">
                  <input type="checkbox" onClick={(e) => checkboxHandler(e)} defaultChecked={groupsDetails?.allCategories} id="allCategories" />
                  <label htmlFor="allCategories" className="chklabel">
                    <span className="mx-2">{t('USER_LEFT_SECTION.FULL_ACCESS_CATEGORIES')}</span>
                  </label>
                </div>
                <div className="checkbox-label margin-auto mx-4 my-2">
                  <input type="checkbox" onClick={(e) => checkboxHandler(e)} defaultChecked={groupsDetails?.allSuppliers} id="allSuppliers" />
                  <label htmlFor="allSuppliers" className="chklabel">
                    <span className="mx-2">{t('USER_LEFT_SECTION.FULL_ACCESS_SUPPLIERS')}</span>
                  </label>
                </div>
                {catBasedOnPTData && (
                  <div className="checkbox-label margin-auto mx-4 my-2">
                    <input type="checkbox" onClick={(e) => checkboxHandler(e)} defaultChecked={groupsDetails?.allProductTypes} id="allProductTypes" />
                    <label htmlFor="allProductTypes" className="chklabel">
                      <span className="mx-2">{t('USER_LEFT_SECTION.FULL_ACCESS_PRODUCTTYPE')} </span>
                    </label>
                  </div>
                )}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <FormGroup>
        <CommonMultiSelect
          label={t('USER_GROUP_VIEW.BRAND')}
          onChange={SelectedBrand}
          selectedValue={selectedBrand}
          optionList={brandsList}
          selectLabel="name"
          selectValue="_id"
          disable={groupsDetails?.allBrands}
          optionListData={brandsList.filter((brand) => brand.isActive === true)}
          fromUser
        />
        {!brandVal && <small className="error-message">{t('USER_LEFT_SECTION.BRAND_ASSIGNMENT_REQUIRED')}</small>}
      </FormGroup>

      {/* Product Type field - show when catBasedOnPTData is true */}
      {catBasedOnPTData && (
        <FormGroup>
          <CommonMultiSelect
            label="Product Type"
            onChange={SelectedProductType}
            selectedValue={selectedProductType}
            optionList={productTypeList}
            selectLabel="name"
            selectValue="_id"
            disable={groupsDetails?.allProductTypes}
          />
          {!productVal && <small className="error-message">{t('USER_LEFT_SECTION.PRODUCT_TYPE_REQUIRED')}</small>}
        </FormGroup>
      )}

      {/* Category 1 - always visible, options filtered based on catBasedOnPTData */}
      <FormGroup>
        <CommonMultiSelect
          label={t('USER_GROUP_VIEW.CAT_ONE')}
          onChange={SelectedCataOne}
          selectedValue={selectedCatOne}
          optionList={catBasedOnPTData ? filteredCatOneList : catOneList}
          selectLabel="name"
          selectValue="_id"
          disable={groupsDetails?.allCategories}
        />
        {!catOneVal && <small className="error-message">{t('USER_LEFT_SECTION.CAT_ONE_ASSIGNMENT_REQUIRED')}</small>}
      </FormGroup>

      <FormGroup>
        <CommonMultiSelect
          label={t('USER_GROUP_VIEW.CAT_TWO')}
          onChange={SelectedCatTwo}
          selectedValue={selectedCatTwo}
          optionList={catBasedOnPTData ? filteredCatTwoList : catTwoList}
          selectLabel="name"
          selectValue="_id"
          disable={groupsDetails?.allCategories}
        />
        {!catTwoVal && <small className="error-message">{t('USER_LEFT_SECTION.CAT_TWO_ASSIGNMENT_REQUIRED')}</small>}
      </FormGroup>

      <FormGroup>
        <CommonMultiSelect
          label={t('USER_GROUP_VIEW.SUPPLIER')}
          onChange={SelectedSupplier}
          selectedValue={selectedSupplier}
          optionList={supplierList}
          selectLabel="name"
          selectValue="_id"
          disable={groupsDetails?.allSuppliers}
        />
        {!supplierVal && <small className="error-message">{t('USER_LEFT_SECTION.SUPPLIER_ASSIGNMENT_REQUIRED')}</small>}
      </FormGroup>
      {/* ***dropdown end*** */}
      {/* ***product archive switch Start*** */}
      <div className="table-responsive table-max-h-60 py-4 mt-4">
        <table className="table custom-table font-13">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontWeight: '500' }}>{t('USER_LEFT_SECTION.TECH_PACK_RIGHT')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="archieveTechPack"
                    defaultChecked={groupsDetails?.archieveTechPack}
                    onClick={(e) => {
                      switchTechPackHandler(e.target.checked, e.target.id);
                    }}
                  />
                  <label className="custom-control-label" htmlFor="archieveTechPack">
                    {t('USER_LEFT_SECTION.ARCHIVE_PRODUCT')}
                  </label>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="openTechPack"
                    defaultChecked={groupsDetails?.openTechPack}
                    onClick={(e) => {
                      switchTechPackHandler(e.target.checked, e.target.id);
                    }}
                  />
                  <label className="custom-control-label" htmlFor="openTechPack">
                    {t('USER_LEFT_SECTION.ACCESS_PRODUCTS')}
                  </label>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="deleteTechPack"
                    defaultChecked={groupsDetails?.deleteTechPack}
                    onClick={(e) => {
                      switchTechPackHandler(e.target.checked, e.target.id);
                    }}
                  />
                  <label className="custom-control-label" htmlFor="deleteTechPack">
                    {t('USER_LEFT_SECTION.MOVE_ARCHIVE_PRODUCT')}
                  </label>
                </div>
              </td>
            </tr>
            <tr>
              <td>
                <div className="custom-control custom-switch">
                  <input
                    type="checkbox"
                    className="custom-control-input"
                    id="trashTechPack"
                    defaultChecked={groupsDetails?.trashTechPack}
                    onClick={(e) => {
                      switchTechPackHandler(e.target.checked, e.target.id);
                    }}
                  />
                  <label className="custom-control-label" htmlFor="trashTechPack">
                    {t('USER_LEFT_SECTION.PERMANENTILY_DELETE_PRODUCTS')}{' '}
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      {/* ***product archive switch end*** */}
      <div className="table-responsive table-max-h-60 pb-2 pb-4">
        <table className="table custom-table font-13">
          <thead>
            <tr>
              <th style={{ textAlign: 'left', fontWeight: '500' }}>{t('USER_LEFT_SECTION.SUBMIT_DOWNLOAD_MANAGEMENT')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                <div className="checkbox-label margin-auto mx-4 my-2">
                  <input
                    type="checkbox"
                    id="submitTechPack"
                    defaultChecked={groupsDetails?.submitTechPack}
                    onClick={(e) => {
                      switchTechPackHandler(e.target.checked, e.target.id);
                    }}
                  />
                  <label htmlFor="submitTechPack" className="chklabel">
                    <span className="mx-2">{t('USER_LEFT_SECTION.SUBMIT_PRODUCT_DATA')}</span>
                  </label>
                </div>
                <div className="checkbox-label margin-auto mx-4 my-2">
                  <input
                    type="checkbox"
                    id="downloadTechPack"
                    defaultChecked={groupsDetails?.downloadTechPack}
                    onClick={(e) => {
                      switchTechPackHandler(e.target.checked, e.target.id);
                    }}
                  />
                  <label htmlFor="downloadTechPack" className="chklabel">
                    <span className="mx-2">{t('USER_LEFT_SECTION.DOWNLOAD_PRODUCT_DATA')}</span>
                  </label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserLeftSection;
